import { buildSpecRows } from './vmkQuoteTable';

/** A4 inner content height (px) – matches quotePdf.css */
const PAGE_INNER_HEIGHT = 1123;
const PAGE_PADDING_Y = 90;
const PAGE_FOOTER_HEIGHT = 22;
const HEADER_HEIGHT_FIRST = 168;
const HEADER_HEIGHT = 112;
const TOTALS_LEGAL_HEIGHT = 268;
const LEGAL_FOOTER_HEIGHT = 196;

const ROW_HEIGHT = 24;
const ROW_HEIGHT_SUBLINE = 36;
/** Double rule between line items (vmk-drawing-divider) */
const ITEM_DIVIDER_HEIGHT = 22;

function getContentLimit({
  isFirstPage,
  isLastPage,
  includePricing = true,
  reserveTailSpace = false,
  tailReserveHeight = null,
}) {
  const header = isFirstPage ? HEADER_HEIGHT_FIRST : HEADER_HEIGHT;
  const footerReserve =
    isLastPage && reserveTailSpace
      ? tailReserveHeight ??
        (includePricing ? TOTALS_LEGAL_HEIGHT : LEGAL_FOOTER_HEIGHT)
      : 0;
  return PAGE_INNER_HEIGHT - PAGE_PADDING_Y - header - PAGE_FOOTER_HEIGHT - footerReserve;
}

function estimateTableHeight(config, includePricing = true, quoteType) {
  const specRows = buildSpecRows(config || {}, quoteType);
  let height = ROW_HEIGHT; // quantity row
  height += ROW_HEIGHT; // location / index row
  specRows.forEach((row) => {
    height += row.subline ? ROW_HEIGHT_SUBLINE : ROW_HEIGHT;
  });
  if (includePricing) {
    height += ROW_HEIGHT; // unit price row
  }
  return height + 12;
}

function estimateDrawingHeight(config, compact) {
  const maxH = compact ? 200 : 360;
  const minH = compact ? 110 : 150;
  const width = config?.width || 1000;
  const height = config?.height || 1000;
  const colWidth = 260;
  const scaled = (height / width) * colWidth;
  return Math.min(maxH, Math.max(minH, scaled));
}

/** Estimated pixel height of one line item on a pricing page */
export function estimateQuoteItemHeight(quote, { compact = false, includePricing = true } = {}) {
  const config = quote?.config || {};
  const tableHeight = estimateTableHeight(config, includePricing, quote?.type);
  const drawingHeight = estimateDrawingHeight(config, compact);
  return Math.ceil(Math.max(tableHeight, drawingHeight));
}

function estimateChunkHeight(quotes, compact, includePricing = true) {
  return quotes.reduce((sum, quote, index) => {
    const divider = index > 0 ? ITEM_DIVIDER_HEIGHT : 0;
    return sum + estimateQuoteItemHeight(quote, { compact, includePricing }) + divider;
  }, 0);
}

/**
 * Pack saved quotes onto A4 pricing pages.
 * Fits as many items per page as possible (typically 2+ for kozijnen).
 * Reserves space on the last page for totals + legal footer.
 */
export function packQuotesIntoPages(
  quotes,
  { includePricing = true, reserveTailSpace = false, tailReserveHeight = null } = {}
) {
  if (!quotes?.length) return [];

  const chunks = [];
  let index = 0;

  while (index < quotes.length) {
    const isFirstPage = chunks.length === 0;
    const chunk = [];

    while (index < quotes.length) {
      const trySize = chunk.length + 1;
      const isLastChunk = index + trySize >= quotes.length;
      const limit = getContentLimit({
        isFirstPage,
        isLastPage: isLastChunk,
        includePricing,
        reserveTailSpace,
        tailReserveHeight,
      });

      const candidate = [...chunk, quotes[index]];
      const compact = candidate.length >= 2;
      const candidateHeight = estimateChunkHeight(candidate, compact, includePricing);

      if (chunk.length > 0 && candidateHeight > limit) {
        break;
      }

      chunk.push(quotes[index]);
      index += 1;

      if (index >= quotes.length) {
        break;
      }

      const nextTrySize = chunk.length + 1;
      const nextIsLastChunk = index + nextTrySize >= quotes.length;
      const nextLimit = getContentLimit({
        isFirstPage,
        isLastPage: nextIsLastChunk,
        includePricing,
        reserveTailSpace,
        tailReserveHeight,
      });
      const nextCandidate = [...chunk, quotes[index]];
      const nextCompact = nextCandidate.length >= 2;
      const nextHeight = estimateChunkHeight(nextCandidate, nextCompact, includePricing);

      if (nextHeight > nextLimit) {
        break;
      }
    }

    chunks.push(chunk);
  }

  return chunks;
}
